import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useEffect, useState } from 'react';
import SupportService from '../services/support.service';
import type { CreateSupportTicketMessage, SupportTicketStatus, UpdateSupportTicketStatus } from '@policy-tracker/types';

/**
 * react-query keys for the TM support ticket views (#2304 part 2). The
 * hierarchy mirrors the existing `TENANT_QUERY_KEYS` shape so the cache
 * invalidation patterns read consistently across the TM UI.
 */
export const SUPPORT_QUERY_KEYS = {
    all: ['support-tickets'] as const,
    lists: () => [...SUPPORT_QUERY_KEYS.all, 'list'] as const,
    list: (params?: { tenantId?: string; status?: SupportTicketStatus }) => [...SUPPORT_QUERY_KEYS.lists(), params] as const,
    details: () => [...SUPPORT_QUERY_KEYS.all, 'detail'] as const,
    detail: (tenantId: string, ticketId: string) => [...SUPPORT_QUERY_KEYS.details(), tenantId, ticketId] as const,
};

/**
 * Lists tickets, optionally filtered by `tenantId` and `status`. Without a
 * `tenantId` filter the server currently returns an empty page — the UI
 * surfaces a tenant picker that supplies the filter.
 */
export function useSupportTicketsList(params?: { tenantId?: string; status?: SupportTicketStatus }) {
    return useQuery({
        queryKey: SUPPORT_QUERY_KEYS.list(params),
        queryFn: () => SupportService.listTickets(params),
        staleTime: 2 * 60 * 1000,
    });
}

/**
 * Fetches a single ticket with its full thread. `enabled` is gated on a
 * non-empty `ticketId` so the hook is safe to call before a row has been
 * selected.
 */
export function useSupportTicketDetail(tenantId: string, ticketId: string) {
    return useQuery({
        queryKey: SUPPORT_QUERY_KEYS.detail(tenantId, ticketId),
        queryFn: () => SupportService.getTicket(tenantId, ticketId),
        enabled: !!tenantId && !!ticketId,
        staleTime: 60 * 1000,
    });
}

/**
 * Posts a TM reply to an open ticket. On success, invalidates the detail
 * query so the new message appears in the thread immediately.
 */
export function useReplyMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ tenantId, ticketId, body }: { tenantId: string; ticketId: string; body: string }) =>
            SupportService.addMessage(tenantId, ticketId, { body } as CreateSupportTicketMessage),
        onSuccess: (_data, vars) => {
            queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.detail(vars.tenantId, vars.ticketId) });
        },
    });
}

/**
 * Closes or reopens a ticket. On success, invalidates both the list (the
 * row's status badge changes) and the detail (the close-fields update).
 */
export function useUpdateStatusMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ tenantId, ticketId, body }: { tenantId: string; ticketId: string; body: UpdateSupportTicketStatus }) =>
            SupportService.updateStatus(tenantId, ticketId, body),
        onSuccess: (_data, vars) => {
            queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.detail(vars.tenantId, vars.ticketId) });
            queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.lists() });
        },
    });
}

/**
 * Returns the current TM admin's Cognito `sub` (used to distinguish "You"
 * vs other TM authors in the detail thread). `null` while loading.
 */
export function useCurrentUserId(): string | null {
    const [userId, setUserId] = useState<string | null>(null);
    useEffect(() => {
        let cancelled = false;
        fetchAuthSession()
            .then((session) => {
                if (cancelled) return;
                const sub = session.tokens?.idToken?.payload?.sub;
                if (typeof sub === 'string') setUserId(sub);
            })
            .catch(() => {
                /* leave null */
            });
        return () => {
            cancelled = true;
        };
    }, []);
    return userId;
}
